import { FORMATIONS, PHASE_DEFENSIVE, existe } from '../data/formations.js';
import { estDisponible } from '../data/postes.js';
import { score } from './adequation.js';

/** Les créneaux d'une formation, vides. */
export function creneaux(formation) {
  const f = FORMATIONS[formation];
  if (!f) throw new Error(`formation inconnue : ${formation}`);
  return f.map((s, i) => ({ index: i, code: s.code, x: s.x, y: s.y, joueurId: null }));
}

/**
 * PRÉ-REMPLISSAGE PAR CRÉNEAU — le cœur de la saisie rapide.
 *
 * Chaque créneau reçoit le dernier joueur ayant démarré À CE CRÉNEAU DANS CETTE
 * FORMATION ; à défaut, le meilleur score d'adéquation parmi les disponibles.
 *
 * L'alternative — un « onze probable » moyenné sur les cinq derniers matchs — a été
 * écartée sur des données réelles : entre le onze de Nice et celui de Brest, cinq
 * joueurs sur onze changent ET la structure change. Un onze moyenné proposerait une
 * défense à quatre le jour où l'entraîneur en aligne trois, soit cinq jetons à
 * corriger plus un changement de formation. Par créneau et par formation, le même
 * historique donne onze sur onze dès la deuxième utilisation du système.
 */
function remplirParCreneau(formation, { historique = [], effectif = [] } = {}) {
  const places = creneaux(formation);
  const pris = new Set();

  // Le plus récent d'abord : un historique trié à l'envers remplirait les créneaux
  // avec le onze du mois dernier.
  const memeForme = [...historique]
    .filter((h) => h.formation === formation && h.titulaires?.length === places.length)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const connu = new Map(effectif.map((j) => [j.id, j]));

  for (const place of places) {
    for (const h of memeForme) {
      const id = h.titulaires[place.index];
      const j = connu.get(id);
      if (!j || pris.has(id) || !estDisponible(j)) continue;
      place.joueurId = id;
      pris.add(id);
      break;
    }
  }

  // Ce que l'historique n'a pas couvert : le meilleur score disponible, créneau par
  // créneau, du plus contraint au moins contraint. Remplir dans l'ordre du tableau
  // donnerait le gardien au premier venu et laisserait la pointe à un défenseur.
  const restants = places.filter((p) => !p.joueurId);
  const libres = () => effectif.filter((j) => !pris.has(j.id) && estDisponible(j));

  restants
    .map((p) => ({ p, choix: libres().map((j) => score(j, p.code)).sort((a, b) => b - a) }))
    .sort((a, b) => (b.choix[0] ?? 0) - (a.choix[0] ?? 0))
    .forEach(({ p }) => {
      let meilleur = null;
      let valeur = -1;
      for (const j of libres()) {
        const v = score(j, p.code);
        if (v > valeur) { valeur = v; meilleur = j; }
      }
      if (meilleur && valeur > 0) { p.joueurId = meilleur.id; pris.add(meilleur.id); }
    });

  return places;
}

/**
 * Les trois états d'une composition. Voir docs/JOUR-DE-MATCH.md.
 *
 * Ils existent pour une raison précise : sans eux, rien ne distingue un onze essayé
 * la veille au soir du onze réellement aligné, et le pré-remplissage apprendrait de
 * brouillons. « C'est la compo » est le seul moment où l'utilisateur AFFIRME quelque
 * chose — tout le reste n'est que manipulation.
 */

export function nouvelleCompo(formation, options = {}) {
  return {
    formation,
    places: remplirParCreneau(formation, options),
    phase: 'offensive',
    etat: 'probable',
  };
}

/** Le terrain vide, créneaux ouverts : un soir de turn-over complet. */
export function viderCompo(compo) {
  return { ...compo, places: compo.places.map((p) => ({ ...p, joueurId: null })), etat: 'probable' };
}

/**
 * Changer de formation sans perdre les joueurs déjà posés.
 *
 * Les joueurs conservés sont réaffectés au créneau où ils valent le plus : passer
 * d'un 4-3-3 à un 3-4-2-1 doit envoyer le latéral droit sur le piston droit, pas le
 * laisser à l'index 4 devenu un poste d'axial.
 */
export function changerFormation(compo, formation, { effectif = [], historique = [] } = {}) {
  if (!existe(formation)) return compo;
  const avant = compo.places.map((p) => p.joueurId).filter(Boolean);
  if (!avant.length) return { ...nouvelleCompo(formation, { effectif, historique }), phase: compo.phase };

  const places = creneaux(formation);
  const connu = new Map(effectif.map((j) => [j.id, j]));
  const dispo = new Set(avant);

  // Affectation gloutonne sur la meilleure paire (créneau, joueur) restante. Ce n'est
  // pas l'optimum global d'un algorithme hongrois, mais sur onze éléments l'écart est
  // invisible et le code se relit.
  const paires = [];
  for (const p of places) {
    for (const id of avant) {
      const j = connu.get(id);
      if (j) paires.push({ i: p.index, id, v: score(j, p.code) });
    }
  }
  paires.sort((a, b) => b.v - a.v);

  const prisCreneau = new Set();
  for (const { i, id, v } of paires) {
    if (v <= 0 || prisCreneau.has(i) || !dispo.has(id)) continue;
    places[i].joueurId = id;
    prisCreneau.add(i);
    dispo.delete(id);
  }

  return { ...compo, formation, places };
}

/** La même composition vue en phase défensive : les jetons glissent, personne ne bouge. */
export function formationDePhase(formation, phase) {
  if (phase !== 'defensive') return formation;
  return PHASE_DEFENSIVE[formation] ?? formation;
}

export function placesEnPhase(compo) {
  const cible = formationDePhase(compo.formation, compo.phase);
  if (cible === compo.formation) return compo.places;
  const f = FORMATIONS[cible];
  if (!f || f.length !== compo.places.length) return compo.places;
  // Index à index : les tableaux de FORMATIONS sont ordonnés pour que l'index i
  // désigne le même joueur dans les deux formes.
  return compo.places.map((p, i) => ({ ...p, code: f[i].code, x: f[i].x, y: f[i].y }));
}

export function placer(compo, index, joueurId) {
  const places = compo.places.map((p) => (p.joueurId === joueurId ? { ...p, joueurId: null } : p));
  places[index] = { ...places[index], joueurId };
  return { ...compo, places };
}

export function echanger(compo, a, b) {
  const places = compo.places.slice();
  const tmp = places[a].joueurId;
  places[a] = { ...places[a], joueurId: places[b].joueurId };
  places[b] = { ...places[b], joueurId: tmp };
  return { ...compo, places };
}

export function retirer(compo, index) {
  const places = compo.places.slice();
  places[index] = { ...places[index], joueurId: null };
  return { ...compo, places };
}

const onzePlaces = (compo) => compo.places.map((p) => p.joueurId).filter(Boolean);
export const estComplete = (compo) => onzePlaces(compo).length === compo.places.length;

/** Le banc : tout le reste, disponibles d'abord, le meilleur remplaçant en tête. */
export function banc(compo, effectif) {
  const places = new Set(onzePlaces(compo));
  return effectif
    .filter((j) => !places.has(j.id))
    .sort((a, b) => {
      const d = Number(estDisponible(b)) - Number(estDisponible(a));
      if (d) return d;
      return (a.numero ?? 99) - (b.numero ?? 99);
    });
}
